/**
 * PermitZonesTab — the Permitting workspace's "Zones & Jurisdictions" tab (2026-09).
 *
 * Add/edit Jurisdictions and Zones from Cortex instead of Airtable directly — the actual ask
 * behind the Permitting workspace. Layout: left panel = searchable Jurisdiction list + "+ New
 * Jurisdiction"; selecting one shows its Zones (+ "+ New Zone") and Departments (+ "+ Add
 * Department") in the middle column; selecting a zone opens its full editable field set in the
 * right pane. Editing an existing Jurisdiction or Zone's shared fields warns first (both are
 * shared across every project that references them — same idiom as ParcelResearchTool.jsx's
 * "Edit shared zone standard?" warning).
 *
 * Field-row helpers here are a small local set (Text/Num/Select/Markdown-with-save), not
 * extracted into a shared module — this tool edits Jurisdiction/Zone records, a different shape
 * than ParcelResearchTool.jsx's Parcel fields, and the helpers are a few lines each.
 *
 * See docs/PERMITTING_WORKSPACE.md.
 */
import { useEffect, useRef, useState } from 'react'
import HeightRulesPanel from './HeightRulesPanel.jsx'
import DemolitionRulesPanel from './DemolitionRulesPanel.jsx'
import ParkingRulesPanel from './ParkingRulesPanel.jsx'
import ParkingStallsPanel from './ParkingStallsPanel.jsx'
import { confirmDialog, alertDialog } from './Dialog.jsx'
import { useActionMenu, ActionMenuButton, ActionMenu, ActionMenuItem } from './ActionMenu.jsx'
import MarkdownField from './MarkdownField.jsx'
import {
  getJurisdictions, getJurisdiction, createJurisdiction, updateJurisdictionField,
  getZonesForJurisdiction, getZone, createZone, updateZoneField,
  getJurisdictionAgencies, addAgency,
  searchDepartmentOrganizations, linkDepartment, unlinkDepartment,
  getZoneParkingCodeRef, setZoneParkingCodeRef,
} from '../services/parcels.js'
import { ctx } from '../outline/ctx.js'

// Shown and edited like the other code references, but stored in Supabase (no Airtable field).
const PARKING_REF = 'Parking Code Reference'
const memberName = () => {
  const f = ctx.currentMember && ctx.currentMember.fields
  return (f && (f['Member Name'] || f['Name'])) || null
}

const STATES = [
  'Alabama', 'Alaska', 'Arizona', 'Arkansas', 'California', 'Colorado', 'Connecticut', 'Delaware',
  'Florida', 'Georgia', 'Hawaii', 'Idaho', 'Illinois', 'Indiana', 'Iowa', 'Kansas', 'Kentucky',
  'Louisiana', 'Maine', 'Maryland', 'Massachusetts', 'Michigan', 'Minnesota', 'Mississippi',
  'Missouri', 'Montana', 'Nebraska', 'Nevada', 'New Hampshire', 'New Jersey', 'New Mexico',
  'New York', 'North Carolina', 'North Dakota', 'Ohio', 'Oklahoma', 'Oregon', 'Pennsylvania',
  'Rhode Island', 'South Carolina', 'South Dakota', 'Tennessee', 'Texas', 'Utah', 'Vermont',
  'Virginia', 'Washington', 'West Virginia', 'Wisconsin', 'Wyoming',
]
const JURIS_TYPES = ['City', 'County', 'Association', 'District', 'State']
const ZONE_TYPES = ['Zone', 'Special Overlay', "Home Owner's Association"]
const SUBMISSION_METHODS = ['online in digital', 'by email in digital', 'in person in digital flash drive', 'in person in physical print']
const EXPEDITING = ['offered', 'not offered']
const AGENCY_TYPES = ['Development (Main Agency)', 'Building', 'Planning', 'HOA/Design Review', 'Fire', 'Public Works', 'Health', 'State', 'Water']

// Fields the pre-design report (api/predesign-report.js) actually reads off a Zone record.
const ZONE_CURRENT_MD = [
  ['Front Yard Setback', 'Front yard setback'],
  ['Rear Yard Setback', 'Rear yard setback'],
  ['Side Yard Setback', 'Side yard setback'],
  ['Additional Setback Information', 'Additional setback notes'],
  ['Maximum Building Height', 'Maximum building height'],
  ['Maximum Lot Coverage', 'Maximum lot coverage'],
  ['Required Principal Building Separation', 'Required principal building separation'],
  ['Parking Stalls Required', 'Parking stalls required'],
  ['Open Space Requirements', 'Open space requirements'],
]
const ZONE_CURRENT_TEXT = [
  ['Setback Code Reference', 'Setback code reference'],
  ['Lot Coverage Code Reference', 'Lot coverage code reference'],
  ['Height Limits Code Reference', 'Height limits code reference'],
  ['Parking Code Reference', 'Parking code reference'],
  ['Building Height Code Link', 'Building height code link (URL)'],
]
// Real, sometimes-populated fields on older zone records — the pre-design report does NOT read
// these (see docs/PARCEL_RESEARCH.md's "Report parity fix"). Shown collapsed, clearly labeled,
// so real content on an existing zone is never silently hidden.
const ZONE_LEGACY_MD = [
  ['General Setback Requirements', 'General setback requirements'],
  ['Accessory Building Setback Requirements', 'Accessory building setback requirements'],
  ['Lot Coverage Requirements', 'Lot coverage requirements'],
  ['Building Height Limits', 'Building height limits'],
]
const ZONE_LEGACY_TEXT = [
  ['Accessory Building Setback Code Reference', 'Accessory building setback code reference'],
]

const has = v => v != null && v !== '' && !(Array.isArray(v) && v.length === 0)
const inputVal = v => (v == null || typeof v === 'object') ? '' : v

export default function PermitZonesTab({ focus } = {}) {
  const [jurisdictions, setJurisdictions] = useState(null)
  const [jurisSearch, setJurisSearch] = useState('')
  const [selJurisId, setSelJurisId] = useState(null)
  const [jurisRecord, setJurisRecord] = useState(null)
  const [agencies, setAgencies] = useState(null)
  const [zones, setZones] = useState(null)
  const [selZoneId, setSelZoneId] = useState(null)
  const [zoneRecord, setZoneRecord] = useState(null)
  const [showAddJuris, setShowAddJuris] = useState(false)
  const [showAddZone, setShowAddZone] = useState(false)
  const [showAddDept, setShowAddDept] = useState(false)
  const [saving, setSaving] = useState('')

  function loadJurisdictions(term) {
    getJurisdictions(term).then(setJurisdictions).catch(() => setJurisdictions([]))
  }
  useEffect(() => { loadJurisdictions('') }, [])
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (focus && focus.jurisdictionId) openJurisdiction(focus.jurisdictionId) }, [focus])

  function openJurisdiction(id) {
    setSelJurisId(id); setSelZoneId(null); setZoneRecord(null)
    setJurisRecord(null); setAgencies(null); setZones(null)
    getJurisdiction(id).then(setJurisRecord).catch(() => {})
    getJurisdictionAgencies(id).then(setAgencies).catch(() => setAgencies([]))
    getZonesForJurisdiction(id).then(setZones).catch(() => setZones([]))
  }
  function openZone(id) {
    setSelZoneId(id); setZoneRecord(null)
    Promise.all([getZone(id), getZoneParkingCodeRef(id).catch(() => '')])
      .then(([rec, parkingRef]) => setZoneRecord({ ...rec, fields: { ...rec.fields, [PARKING_REF]: parkingRef } }))
      .catch(() => {})
  }
  function refreshAgencies() {
    if (selJurisId) getJurisdictionAgencies(selJurisId).then(setAgencies).catch(() => {})
  }
  async function removeDepartment(org) {
    const name = org.fields['Organization Name'] || 'this department'
    const ok = await confirmDialog({
      title: 'Remove department?',
      message: `Remove "${name}" from ${jurisRecord?.fields?.Name || 'this jurisdiction'}? This list belongs to the jurisdiction, so it comes off every project there. The organization itself is not deleted and stays linked anywhere else it is used.`,
      confirmLabel: 'Remove', danger: true,
    })
    if (!ok) return
    setAgencies(list => (list || []).filter(a => a.id !== org.id))
    try { await unlinkDepartment(selJurisId, org.id) } catch { /* the re-read below shows the truth */ }
    refreshAgencies()
  }
  function refreshZoneList() {
    if (selJurisId) getZonesForJurisdiction(selJurisId).then(setZones)
  }

  async function saveJurisField(field, value) {
    setSaving(field)
    const ok = await confirmDialog({
      title: 'Edit shared jurisdiction record?',
      message: `"${jurisRecord?.fields?.Name || 'This jurisdiction'}" is shared by every zone and parcel that references it. Editing this changes it everywhere.`,
      confirmLabel: 'Save', danger: true,
    })
    if (!ok) { setSaving(''); return }
    try {
      await updateJurisdictionField(selJurisId, { [field]: value })
      setJurisRecord(r => ({ ...r, fields: { ...r.fields, [field]: value } }))
    } catch (e) {
      await alertDialog({ title: 'Not saved', message: `Could not save ${field}: ${e?.message || 'unknown error'}` })
      setJurisRecord(r => ({ ...r }))
    }
    setSaving('')
  }
  async function saveZoneField(field, value) {
    setSaving(field)
    const ok = await confirmDialog({
      title: 'Edit shared zone record?',
      message: `"${zoneRecord?.fields?.['Name of Zone'] || 'This zone'}" is shared by every parcel in this zone. Editing this changes it for all of them.`,
      confirmLabel: 'Save', danger: true,
    })
    if (!ok) { setSaving(''); return }
    try {
      // The parking code reference has no Airtable field; it lives in Supabase (database/280).
      if (field === PARKING_REF) await setZoneParkingCodeRef(selZoneId, value || '', memberName())
      else await updateZoneField(selZoneId, { [field]: value })
      setZoneRecord(r => ({ ...r, fields: { ...r.fields, [field]: value } }))
      refreshZoneList()
    } catch (e) {
      // Before 2026-10-01 a failed save was swallowed here, so an entry looked saved and wasn't.
      await alertDialog({ title: 'Not saved', message: `Could not save ${field}: ${e?.message || 'unknown error'}` })
      setZoneRecord(r => ({ ...r }))
    }
    setSaving('')
  }

  const S = STYLES
  return (
    <div style={S.wrap}>
      <div style={S.leftCol}>
        <div style={S.colHeader}>
          <input style={S.search} placeholder="Search jurisdictions…" value={jurisSearch}
            onChange={e => { setJurisSearch(e.target.value); loadJurisdictions(e.target.value) }} />
          <button style={S.primaryBtn} onClick={() => setShowAddJuris(true)}>+ New Jurisdiction</button>
        </div>
        <div style={S.list}>
          {jurisdictions == null && <div style={S.muted}>Loading…</div>}
          {jurisdictions && jurisdictions.length === 0 && <div style={S.muted}>No jurisdictions found.</div>}
          {jurisdictions && jurisdictions.map(j => (
            <button key={j.id} style={{ ...S.listItem, ...(selJurisId === j.id ? S.listItemActive : {}) }}
              onClick={() => openJurisdiction(j.id)}>
              <div style={{ fontWeight: 500 }}>{j.fields['Name'] || '—'}</div>
              <div style={S.listItemSub}>{j.fields['Type'] || ''}{j.fields['State'] ? ' · ' + j.fields['State'] : ''}</div>
            </button>
          ))}
        </div>
      </div>

      <div style={S.midCol}>
        {!selJurisId && <div style={S.muted}>Select a jurisdiction, or create a new one.</div>}
        {selJurisId && jurisRecord && (
          <>
            <div style={S.midHeader}>
              <button style={{ ...S.textBtn, fontWeight: selZoneId ? 400 : 600 }} onClick={() => { setSelZoneId(null); setZoneRecord(null) }}>
                {jurisRecord.fields['Name'] || 'Jurisdiction'}
              </button>
            </div>
            <div style={S.subLabel}>Zones <button style={S.smallAddBtn} onClick={() => setShowAddZone(true)}>+ New Zone</button></div>
            {zones == null && <div style={S.muted}>Loading…</div>}
            {zones && zones.length === 0 && <div style={S.muted}>No zones yet.</div>}
            {zones && zones.map(z => (
              <button key={z.id} style={{ ...S.listItem, ...(selZoneId === z.id ? S.listItemActive : {}) }} onClick={() => openZone(z.id)}>
                <div style={{ fontWeight: 500 }}>{z.fields['Name of Zone'] || '—'}</div>
                <div style={S.listItemSub}>{z.fields['Type'] || 'Zone'}</div>
              </button>
            ))}
            <div style={{ ...S.subLabel, marginTop: 18 }}>Departments <button style={S.smallAddBtn} onClick={() => setShowAddDept(true)}>+ Add Department</button></div>
            {agencies == null && <div style={S.muted}>Loading…</div>}
            {agencies && agencies.length === 0 && <div style={S.muted}>No departments linked yet.</div>}
            {agencies && agencies.map(a => (
              <DepartmentCard key={a.id} org={a} onRemove={() => removeDepartment(a)} />
            ))}
          </>
        )}
      </div>

      <div style={S.rightCol}>
        {!selJurisId && (
          <div style={{ maxWidth: 640 }}>
            <div style={S.muted}>Zone or jurisdiction details will appear here.</div>
            <ParkingStallsPanel
              scope="firm" title="Firm default parking stall sizes" byName={memberName()}
              hint="Used by a project's model until its jurisdiction has stall sizes recorded (the model marks those as not verified). Changes need the Permitting or Admin seat."
            />
          </div>
        )}
        {selJurisId && !selZoneId && jurisRecord && (
          <JurisdictionFields record={jurisRecord} onSave={saveJurisField} saving={saving} />
        )}
        {selZoneId && !zoneRecord && <div style={S.muted}>Loading zone…</div>}
        {selZoneId && zoneRecord && (
          <ZoneFields record={zoneRecord} onSave={saveZoneField} saving={saving} />
        )}
      </div>

      {showAddJuris && (
        <AddJurisdictionModal
          onClose={() => setShowAddJuris(false)}
          onCreated={id => { setShowAddJuris(false); loadJurisdictions(jurisSearch); openJurisdiction(id) }}
        />
      )}
      {showAddZone && selJurisId && (
        <AddZoneModal
          jurisdictionId={selJurisId}
          onClose={() => setShowAddZone(false)}
          onCreated={id => { setShowAddZone(false); refreshZoneList(); openZone(id) }}
        />
      )}
      {showAddDept && selJurisId && (
        <AddDepartmentModal
          jurisdictionId={selJurisId}
          linkedIds={(agencies || []).map(a => a.id)}
          onClose={() => setShowAddDept(false)}
          onCreated={() => { setShowAddDept(false); refreshAgencies() }}
        />
      )}
    </div>
  )
}

/* ── Field renderers (shared shape for Jurisdiction + Zone editing) ─────────────────────── */

function Row({ label, children, saving }) {
  return (
    <div style={STYLES.row}>
      <div style={STYLES.rowLabel}>{label}</div>
      <div style={STYLES.rowField}>{children}{saving && <span style={STYLES.savingTag}>saving…</span>}</div>
    </div>
  )
}
function Text({ label, field, fields, onSave, saving }) {
  const cur = inputVal(fields[field])
  return (
    <Row label={label} saving={saving === field}>
      <input style={STYLES.input} defaultValue={cur} key={field + cur}
        onBlur={e => { const v = e.target.value; if (String(v) !== String(cur)) onSave(field, v === '' ? null : v) }} />
    </Row>
  )
}
function Num({ label, field, fields, onSave, saving }) {
  const cur = inputVal(fields[field])
  return (
    <Row label={label} saving={saving === field}>
      <input type="number" style={STYLES.input} defaultValue={cur} key={field + cur}
        onBlur={e => { const v = e.target.value; if (String(v) !== String(cur)) onSave(field, v === '' ? null : Number(v)) }} />
    </Row>
  )
}
function Select({ label, field, options, fields, onSave, saving }) {
  const cur = typeof fields[field] === 'object' ? '' : (fields[field] || '')
  return (
    <Row label={label} saving={saving === field}>
      <select style={STYLES.input} value={cur} onChange={e => onSave(field, e.target.value || null)}>
        <option value="">—</option>
        {options.map(o => <option key={o} value={o}>{o}</option>)}
      </select>
    </Row>
  )
}
function MdField({ label, field, fields, onSave, saving }) {
  const [val, setVal] = useState(fields[field] || '')
  const [dirty, setDirty] = useState(false)
  useEffect(() => { setVal(fields[field] || ''); setDirty(false) }, [fields[field]])
  return (
    <div style={{ marginBottom: 14 }}>
      <div style={STYLES.label}>{label}</div>
      <MarkdownField value={val} onChange={v => { setVal(v); setDirty(true) }} placeholder="Type Markdown…" />
      {dirty && (
        <button style={{ ...STYLES.primaryBtn, marginTop: 6 }} disabled={saving === field} onClick={() => onSave(field, val)}>
          {saving === field ? 'Saving…' : 'Save'}
        </button>
      )}
    </div>
  )
}

function JurisdictionFields({ record, onSave, saving }) {
  const f = record.fields || {}
  return (
    <div style={{ maxWidth: 640 }}>
      <div style={STYLES.sectionTitle}>Jurisdiction</div>
      <Text label="Name" field="Name" fields={f} onSave={onSave} saving={saving} />
      <Select label="Type" field="Type" options={JURIS_TYPES} fields={f} onSave={onSave} saving={saving} />
      <Select label="State" field="State" options={STATES} fields={f} onSave={onSave} saving={saving} />
      <Text label="Main Street Address" field="Main Street Address" fields={f} onSave={onSave} saving={saving} />
      <Text label="Main City" field="Main City" fields={f} onSave={onSave} saving={saving} />
      <Text label="Main Zip" field="Main Zip" fields={f} onSave={onSave} saving={saving} />
      <Text label="Online Municipal Code (URL)" field="Online Municipal Code" fields={f} onSave={onSave} saving={saving} />
      <Text label="Online GIS Parcel Search (URL)" field="Online GIS Parcel Search" fields={f} onSave={onSave} saving={saving} />
      <ParkingStallsPanel
        scope="jurisdiction" scopeId={record.id} title="Required parking stall sizes" byName={memberName()}
        hint="The city's rule — every project in this jurisdiction draws these stalls around its cars in the model. A zone or overlay can override below."
      />
      <ParkingRulesPanel kind="jurisdiction" sourceId={record.id} sourceName={f['Name'] || f['Jurisdiction Name'] || ''} byName={memberName()} />
      <DemolitionRulesPanel jurisdictionId={record.id} byName={memberName()} />
    </div>
  )
}

function ZoneFields({ record, onSave, saving }) {
  const f = record.fields || {}
  const [showLegacy, setShowLegacy] = useState(false)
  return (
    <div style={{ maxWidth: 640 }}>
      <div style={STYLES.sectionTitle}>Zone</div>
      <Text label="Name of Zone" field="Name of Zone" fields={f} onSave={onSave} saving={saving} />
      <Select label="Type" field="Type" options={ZONE_TYPES} fields={f} onSave={onSave} saving={saving} />
      <Num label="Minimum Lot Width (feet)" field="Minimum Lot Width Feet" fields={f} onSave={onSave} saving={saving} />
      <Num label="Minimum Lot Depth (feet)" field="Minimum Lot Depth Feet" fields={f} onSave={onSave} saving={saving} />
      <Num label="Minimum Lot Area (SF)" field="Minimum Lot Area SF" fields={f} onSave={onSave} saving={saving} />

      <div style={{ ...STYLES.sectionTitle, marginTop: 20 }}>Current — used in the pre-design report</div>
      {ZONE_CURRENT_MD.map(([field, label]) => (
        <MdField key={field} label={label} field={field} fields={f} onSave={onSave} saving={saving} />
      ))}
      {ZONE_CURRENT_TEXT.map(([field, label]) => (
        <Text key={field} label={label} field={field} fields={f} onSave={onSave} saving={saving} />
      ))}

      <ParkingStallsPanel
        scope="zone" scopeId={record.id} title="Parking stall size overrides" byName={memberName()}
        hint="Only when this zone or overlay changes the jurisdiction's stall sizes — leave a row blank to use the jurisdiction's."
      />

      <HeightRulesPanel kind="zone" sourceId={record.id} sourceName={f['Name'] || f['Name of Zone'] || ''} byName={memberName()} />

      <ParkingRulesPanel kind="zone" sourceId={record.id} sourceName={f['Name'] || f['Name of Zone'] || ''} byName={memberName()} />

      <button style={STYLES.textBtn} onClick={() => setShowLegacy(s => !s)}>
        {showLegacy ? '▾' : '▸'} Legacy fields — not read by the report
      </button>
      {showLegacy && (
        <div style={{ marginTop: 8 }}>
          {ZONE_LEGACY_MD.map(([field, label]) => (
            <MdField key={field} label={label} field={field} fields={f} onSave={onSave} saving={saving} />
          ))}
          {ZONE_LEGACY_TEXT.map(([field, label]) => (
            <Text key={field} label={label} field={field} fields={f} onSave={onSave} saving={saving} />
          ))}
        </div>
      )}
    </div>
  )
}

/* ── Create modals ────────────────────────────────────────────────────────────────────── */

function ModalShell({ title, onClose, onSubmit, submitLabel, disabled, children }) {
  return (
    <div style={STYLES.overlay} onClick={onClose}>
      <div style={STYLES.modal} onClick={e => e.stopPropagation()}>
        <div style={STYLES.modalTitle}>{title}</div>
        <div style={{ maxHeight: '70vh', overflowY: 'auto' }}>{children}</div>
        <div style={{ display: 'flex', gap: 8, marginTop: 16, justifyContent: 'flex-end' }}>
          <button style={STYLES.ghostBtn} onClick={onClose}>Cancel</button>
          <button style={STYLES.primaryBtn} disabled={disabled} onClick={onSubmit}>{submitLabel}</button>
        </div>
      </div>
    </div>
  )
}

function AddJurisdictionModal({ onClose, onCreated }) {
  const [name, setName] = useState('')
  const [type, setType] = useState('City')
  const [state, setState] = useState('California')
  const [saving, setSaving] = useState(false)

  async function submit() {
    if (!name.trim()) return
    setSaving(true)
    try {
      const rec = await createJurisdiction({ Name: name.trim(), Type: type, State: state })
      onCreated(rec.id)
    } finally { setSaving(false) }
  }
  return (
    <ModalShell title="New Jurisdiction" onClose={onClose} onSubmit={submit} submitLabel={saving ? 'Creating…' : 'Create'} disabled={saving || !name.trim()}>
      <Row label="Name"><input style={STYLES.input} value={name} onChange={e => setName(e.target.value)} autoFocus /></Row>
      <Row label="Type">
        <select style={STYLES.input} value={type} onChange={e => setType(e.target.value)}>
          {JURIS_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </Row>
      <Row label="State">
        <select style={STYLES.input} value={state} onChange={e => setState(e.target.value)}>
          {STATES.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </Row>
    </ModalShell>
  )
}

function AddZoneModal({ jurisdictionId, onClose, onCreated }) {
  const [name, setName] = useState('')
  const [type, setType] = useState('Zone')
  const [saving, setSaving] = useState(false)

  async function submit() {
    if (!name.trim()) return
    setSaving(true)
    try {
      const rec = await createZone(jurisdictionId, { 'Name of Zone': name.trim(), Type: type })
      onCreated(rec.id)
    } finally { setSaving(false) }
  }
  return (
    <ModalShell title="New Zone" onClose={onClose} onSubmit={submit} submitLabel={saving ? 'Creating…' : 'Create'} disabled={saving || !name.trim()}>
      <Row label="Name of Zone"><input style={STYLES.input} value={name} onChange={e => setName(e.target.value)} placeholder="e.g. R-1" autoFocus /></Row>
      <Row label="Type">
        <select style={STYLES.input} value={type} onChange={e => setType(e.target.value)}>
          {ZONE_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </Row>
      <div style={STYLES.muted}>Setback/height/coverage narratives and code references are edited after creation.</div>
    </ModalShell>
  )
}

function DepartmentCard({ org, onRemove }) {
  const menu = useActionMenu()
  const f = org.fields || {}
  const sub = f['Agency Type'] || ([].concat(f['Type'] || []).includes('HOA') ? 'HOA' : '')
  return (
    <div className="aj-dept-card" style={{ ...STYLES.deptCard, display: 'flex', alignItems: 'flex-start', gap: 6 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 500 }}>{f['Organization Name'] || 'Department'} <span style={{ color: 'var(--aj-text-muted)', fontWeight: 400 }}>{sub}</span></div>
        <div style={STYLES.mono}>{[f['Phone'], f['General Email']].filter(has).join('  ·  ') || '—'}</div>
      </div>
      <ActionMenuButton menu={menu} title="Department actions" />
      <ActionMenu menu={menu}>
        <ActionMenuItem danger onClick={() => { menu.close(); onRemove() }}>Remove from jurisdiction</ActionMenuItem>
      </ActionMenu>
    </div>
  )
}

/**
 * "Add Department": pick an EXISTING organization (the default — most departments, HOAs, and
 * county-wide agencies like OCFA already exist and serve several jurisdictions), or create one.
 */
function AddDepartmentModal({ jurisdictionId, linkedIds, onClose, onCreated }) {
  const [mode, setMode] = useState('existing')
  return mode === 'existing'
    ? <LinkDepartmentModal jurisdictionId={jurisdictionId} linkedIds={linkedIds} onClose={onClose} onCreated={onCreated} onCreateNew={() => setMode('new')} />
    : <CreateDepartmentModal jurisdictionId={jurisdictionId} onClose={onClose} onCreated={onCreated} onBack={() => setMode('existing')} />
}

function LinkDepartmentModal({ jurisdictionId, linkedIds, onClose, onCreated, onCreateNew }) {
  const [term, setTerm] = useState('')
  const [results, setResults] = useState(null)
  const [picked, setPicked] = useState(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')
  const seq = useRef(0)
  const linked = new Set(linkedIds || [])

  useEffect(() => {
    const t = term.trim()
    if (t.length < 2) { setResults(null); return }
    const my = ++seq.current
    const h = setTimeout(() => {
      searchDepartmentOrganizations(t)
        .then(r => { if (my === seq.current) setResults(r) })
        .catch(() => { if (my === seq.current) setResults([]) })
    }, 250)
    return () => clearTimeout(h)
  }, [term])

  async function submit() {
    if (!picked) return
    setSaving(true); setError('')
    try { await linkDepartment(jurisdictionId, picked.id); onCreated() }
    catch (e) { setError('Could not add: ' + (e?.message || 'unknown error')); setSaving(false) }
  }
  return (
    <ModalShell title="Add Department" onClose={onClose} onSubmit={submit}
      submitLabel={saving ? 'Adding…' : 'Add'} disabled={saving || !picked}>
      <div style={STYLES.label}>Find an existing organization</div>
      <input style={{ ...STYLES.search, marginBottom: 8 }} value={term} autoFocus
        placeholder="Type a name, e.g. OCFA or an HOA…"
        onChange={e => { setTerm(e.target.value); setPicked(null) }} />
      <div style={STYLES.pickList}>
        {results == null && <div style={STYLES.muted}>Type at least two letters to search.</div>}
        {results && results.length === 0 && <div style={STYLES.muted}>No organizations match.</div>}
        {results && results.map(o => {
          const already = linked.has(o.id)
          const on = picked && picked.id === o.id
          return (
            <button key={o.id} disabled={already}
              style={{ ...STYLES.pickItem, ...(on ? STYLES.listItemActive : {}), ...(already ? { opacity: 0.5, cursor: 'default' } : {}) }}
              onClick={() => setPicked(o)}>
              <div style={{ fontWeight: 500 }}>{o.name}</div>
              <div style={STYLES.listItemSub}>
                {[o.types.join(', '), o.city].filter(Boolean).join(' · ') || 'No type set'}
                {already ? ' · already on this list' : ''}
              </div>
            </button>
          )
        })}
      </div>
      {error && <div style={{ color: '#b42318', fontSize: 12, marginTop: 8 }}>{error}</div>}
      <button style={{ ...STYLES.smallAddBtn, marginTop: 10, padding: 0 }} onClick={onCreateNew}>
        Not listed? Create a new department
      </button>
    </ModalShell>
  )
}

function CreateDepartmentModal({ jurisdictionId, onClose, onCreated, onBack }) {
  const [f, setF] = useState({
    'Organization Name': '', 'Agency Type': 'Planning', 'Client Address': '', 'Client City': '',
    'Client State': 'California', 'Client Zip': '', 'Phone': '', 'General Email': '',
    'Submission Method': 'online in digital', 'Review Turnaround In Days': '', 'Plan Check Expediting': 'not offered',
  })
  const [saving, setSaving] = useState(false)
  const set = (k, v) => setF(prev => ({ ...prev, [k]: v }))

  async function submit() {
    if (!f['Organization Name'].trim()) return
    setSaving(true)
    try {
      const fields = { ...f }
      if (fields['Review Turnaround In Days'] === '') delete fields['Review Turnaround In Days']
      else fields['Review Turnaround In Days'] = Number(fields['Review Turnaround In Days'])
      await addAgency(jurisdictionId, fields)
      onCreated()
    } finally { setSaving(false) }
  }
  return (
    <ModalShell title="New Department" onClose={onClose} onSubmit={submit} submitLabel={saving ? 'Creating…' : 'Create'} disabled={saving || !f['Organization Name'].trim()}>
      <button style={{ ...STYLES.smallAddBtn, padding: 0, marginBottom: 8 }} onClick={onBack}>← Pick an existing organization instead</button>
      <Row label="Organization Name"><input style={STYLES.input} value={f['Organization Name']} onChange={e => set('Organization Name', e.target.value)} autoFocus /></Row>
      <Row label="Agency Type">
        <select style={STYLES.input} value={f['Agency Type']} onChange={e => set('Agency Type', e.target.value)}>
          {AGENCY_TYPES.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </Row>
      <Row label="Street Address"><input style={STYLES.input} value={f['Client Address']} onChange={e => set('Client Address', e.target.value)} /></Row>
      <Row label="City"><input style={STYLES.input} value={f['Client City']} onChange={e => set('Client City', e.target.value)} /></Row>
      <Row label="State">
        <select style={STYLES.input} value={f['Client State']} onChange={e => set('Client State', e.target.value)}>
          {STATES.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </Row>
      <Row label="Zip"><input style={STYLES.input} value={f['Client Zip']} onChange={e => set('Client Zip', e.target.value)} /></Row>
      <Row label="Phone"><input style={STYLES.input} value={f['Phone']} onChange={e => set('Phone', e.target.value)} /></Row>
      <Row label="General Email"><input style={STYLES.input} value={f['General Email']} onChange={e => set('General Email', e.target.value)} /></Row>
      <Row label="Submission Method">
        <select style={STYLES.input} value={f['Submission Method']} onChange={e => set('Submission Method', e.target.value)}>
          {SUBMISSION_METHODS.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </Row>
      <Row label="Review Turnaround (days)"><input type="number" style={STYLES.input} value={f['Review Turnaround In Days']} onChange={e => set('Review Turnaround In Days', e.target.value)} /></Row>
      <Row label="Plan Check Expediting">
        <select style={STYLES.input} value={f['Plan Check Expediting']} onChange={e => set('Plan Check Expediting', e.target.value)}>
          {EXPEDITING.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
      </Row>
    </ModalShell>
  )
}

const STYLES = {
  wrap: { display: 'flex', height: '100%', fontFamily: '"DM Sans",sans-serif' },
  leftCol: { width: 260, borderRight: '0.5px solid var(--aj-border)', display: 'flex', flexDirection: 'column', flexShrink: 0 },
  midCol: { width: 300, borderRight: '0.5px solid var(--aj-border)', padding: '14px 16px', overflowY: 'auto', flexShrink: 0 },
  rightCol: { flex: 1, padding: '14px 20px', overflowY: 'auto' },
  colHeader: { padding: '12px 12px 8px', display: 'flex', flexDirection: 'column', gap: 8 },
  list: { flex: 1, overflowY: 'auto', padding: '0 8px 12px' },
  listItem: { display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px', border: 'none', borderRadius: 6, background: 'transparent', cursor: 'pointer', marginBottom: 2, fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' },
  listItemActive: { background: 'var(--aj-bg-subtle)' },
  listItemSub: { fontSize: 11, color: 'var(--aj-text-muted)', marginTop: 2 },
  midHeader: { fontSize: 14, fontWeight: 600, marginBottom: 10, color: 'var(--aj-text)' },
  subLabel: { fontSize: 11, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--aj-text-mid)', marginBottom: 6, display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  deptCard: { padding: '8px 10px', border: '0.5px solid var(--aj-border)', borderRadius: 8, marginBottom: 6 },
  mono: { fontFamily: '"DM Mono",monospace', fontSize: 11, color: 'var(--aj-text-mid)' },
  muted: { color: 'var(--aj-text-muted)', fontSize: 13, padding: '8px 0' },
  sectionTitle: { fontSize: 13, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 8 },
  row: { display: 'flex', alignItems: 'center', gap: 14, padding: '7px 0', borderBottom: '0.5px solid var(--aj-border)' },
  rowLabel: { flex: '0 0 220px', maxWidth: 220, fontSize: 12, color: 'var(--aj-text-mid)' },
  rowField: { flex: 1, display: 'flex', alignItems: 'center', gap: 8 },
  label: { fontSize: 11, fontWeight: 500, color: 'var(--aj-text-mid)', marginBottom: 5 },
  savingTag: { fontSize: 10, color: 'var(--aj-text-muted)' },
  input: { flex: 1, minWidth: 0, boxSizing: 'border-box', fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '7px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' },
  search: { width: '100%', boxSizing: 'border-box', fontFamily: '"DM Sans",sans-serif', fontSize: 13, padding: '7px 9px', borderRadius: 4, border: '0.5px solid var(--aj-border-mid)', color: 'var(--aj-text)', background: 'var(--aj-bg-subtle)' },
  primaryBtn: { border: 'none', background: '#07272D', color: '#fff', cursor: 'pointer', borderRadius: 4, padding: '7px 12px', fontSize: 12, fontWeight: 500, fontFamily: '"DM Sans",sans-serif' },
  ghostBtn: { border: '0.5px solid var(--aj-border-mid)', background: 'var(--aj-bg-elevated)', color: 'var(--aj-text)', cursor: 'pointer', borderRadius: 4, padding: '7px 12px', fontSize: 12, fontWeight: 500, fontFamily: '"DM Sans",sans-serif' },
  smallAddBtn: { border: 'none', background: 'transparent', color: '#1D6FA8', cursor: 'pointer', fontSize: 11, fontFamily: '"DM Sans",sans-serif', fontWeight: 500 },
  pickList: { maxHeight: 300, overflowY: 'auto', border: '0.5px solid var(--aj-border)', borderRadius: 4, padding: 4 },
  pickItem: { display: 'block', width: '100%', textAlign: 'left', padding: '7px 9px', border: 'none', borderRadius: 4, background: 'transparent', cursor: 'pointer', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: 'var(--aj-text)' },
  textBtn: { border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--aj-text-mid)', fontSize: 12, fontFamily: '"DM Sans",sans-serif', padding: '4px 0', textAlign: 'left' },
  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 9000, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  modal: { width: 460, maxWidth: '92vw', background: 'var(--aj-bg-elevated)', borderRadius: 10, padding: 20, boxShadow: '0 8px 30px rgba(0,0,0,0.25)' },
  modalTitle: { fontSize: 15, fontWeight: 600, color: 'var(--aj-text)', marginBottom: 14 },
}
